'use client'

import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {Section} from '@/components/common/content/Section'
import {Hero} from '@/components/common/hero/Hero'
import {Page} from '@/components/Page'
import {QuestionsAndAnswersRelatedQuestions} from '@/components/questions-and-answers/QuestionsAndAnswersRelatedQuestions'
import {SpecimenGuideBanner} from '@/components/specimen-guide/SpecimenGuideBanner'
import {SpecimenGuideContent} from '@/components/specimen-guide/SpecimenGuideContent'
import {SpecimenGuidePopularSectors} from '@/components/specimen-guide/SpecimenGuidePopularSectors'
import {useCommonContent} from '@/content/common'
import {getSpecimenGuideContent} from '@/content/specimenGuide'
import {useTranslation} from '@/i18n'
import {QuestionsAndAnswersContentType} from '@/utils/questions-and-answers/constants'
import {QuestionsAndAnswersRelatedQuestion} from '@/utils/questions-and-answers/types'
import {getPopularSectorsByIds} from '@/utils/specimen-guide/helpers'
import {SpecimenGuideSector} from '@/utils/specimen-guide/types'

type SpecimenGuideForSectorProps = {
  sector: SpecimenGuideSector
  relatedQuestions: QuestionsAndAnswersRelatedQuestion[]
  breadcrumbTrail: BreadcrumbStep[]
}

const SpecimenGuideForSector: FC<SpecimenGuideForSectorProps> = ({
  sector,
  relatedQuestions,
  breadcrumbTrail,
}) => {
  const {t} = useTranslation()
  const {
    guide: {hero, faqs},
  } = getSpecimenGuideContent()
  const {faqsTitle} = useCommonContent()

  const sectorName = sector.name
  const popularSectors = getPopularSectorsByIds(sector.popularSectors)

  return (
    <Page>
      <Hero
        title={t(hero.title, {name: sectorName})}
        subtitle={t(hero.sectorSubtitle, {
          name: sectorName,
        })}
        breadcrumbs={<Breadcrumbs trail={breadcrumbTrail} hideCurrent />}
        maxWidth="100%"
      />
      <Section hasPaddingTop>
        <SpecimenGuideContent name={sectorName} text={sector.text} specimens={sector.specimens} />
      </Section>
      {relatedQuestions.length > 0 && (
        <Section hasPaddingTop isHighlighted>
          <QuestionsAndAnswersRelatedQuestions
            type={QuestionsAndAnswersContentType.trademark}
            title={t(faqsTitle)}
            relatedQuestions={relatedQuestions}
            buttonLinkHref={faqs.buttonLinkHref}
            buttonLinkText={t(faqs.buttonLinkText)}
            trackedEvent={{
              name: 'All specimen questions button clicked',
              parameters: {sector: sectorName},
            }}
          />
        </Section>
      )}
      {popularSectors.length > 0 && (
        <Section hasPaddingTop>
          <SpecimenGuidePopularSectors popularSectors={popularSectors} />
        </Section>
      )}
      <Section hasPaddingTop hasPaddingBottom>
        <SpecimenGuideBanner />
      </Section>
    </Page>
  )
}

export default SpecimenGuideForSector
